import { useState } from 'react'
import ReactHtmlParser from 'react-html-parser'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { Typography } from 'common/typography'
import { SecondaryButton } from 'common/button'
import { useCourse } from 'querys/course'
import Wrapper from 'common/wrapper'
import useIntersectionObserver from 'hooks/use-intersection-observer'
import AnimatedGradient from 'common/animations/animated-gradient'
import CourseProjectModal from './course-project-modal'

const CourseProjectStyled = styled.section`
  grid-area: project;
  font: var(--regular);
  overflow: hidden;
  padding: 64px 0 0 0;
  h2 {
    margin-top: 40px;
    ${AnimatedGradient}
  }
  .project-circle {
    height: calc(100vw - 32px);
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--secondary140);
    border-radius: 50%;
  }
  .project-presentation {
    position: relative;
    margin: 16px 0;
    width: max-content;
    max-width: 100%;
    transform: scale(1);
  }
  .project-presenter {
    max-width: 100%;
    height: auto;
  }
  .project-projection {
    position: absolute;
    background-size: cover;
    top: 27.4%;
    bottom: 17%;
    height: auto;
    right: 26.5%;
    left: 0%;
    width: auto;
    background-image: ${({ thumb }) => `url(${thumb})`};
  }
  .project-descriptions {
    padding-left: 20px;
  }
  .project-presenter {
    max-width: 100%;
    position: relative;
    z-index: 1;
  }
  .project-description {
    margin: 24px 0;
    position: relative;
    &::before {
      position: absolute;
      content: '';
      left: -20px;
      top: 6px;
      display: block;
      width: 12px;
      height: 12px;
      background: var(--turquoise);
      border-radius: 50%;
    }
    &:first-of-type {
      margin-top: 16px;
    }
  }
  button {
    margin-top: 8px;
  }
  @media(min-width: 768px) {
    padding: 80px 0;
    .project-circle {
      height: calc(100vw - 6em);
    }
    .project-description {
      margin: 48px 0;
      &:first-of-type {
        margin-top: 48px;
      }
    }
    .project-presentation {
      transform: scale(1.5);
    }
    h2 {
      margin-top: 50px;
    }
  }
  @media(min-width: 1024px) {
    position: relative;
    .project-circle {
      position: absolute;
      right: 0;
      height: 100%;
      max-height: 560px;
      max-width: 560px;
      width: calc(100% - 464px);
    }
    .project-content {
      max-width: 400px;
    }
    .project-presentation {
      transform: scale(1);
    }
    .project-description {
      margin: 64px 0;
      &:first-of-type {
        margin-top: 64px;
      }
    }
  }
  @media(min-width: 1150px) {
    position: static;
    .project-section-container {
      position: relative;
    }
    .project-presentation {
      transform: scale(1.2);
    }
  }
  @media(min-width: 1366px) {
    position: static;
    .project-section-container {
      position: relative;
    }
    .project-circle {
      top: 15%;
      transform: scale(1.5);
    }
    .project-presentation {
      transform: scale(0.9) translate(-15%, 0%);
    }
  }
`

function CourseProject({ setActive }) {
  const [showProject, setShowProject] = useState(false)
  const { data } = useCourse()

  const { ref: projectRef } = useIntersectionObserver({
    onIntersect: () => setActive('proyecto'),
    rootMargin: '0px 0px -100px 0px',
  })

  if (!data.project) return null

  const { title, thumb_image, description: { blocks } } = data.project

  const isList = blocks[0].type === 'list'

  return (
    <CourseProjectStyled aria-labelledby="project-title" id="proyecto" ref={projectRef} thumb={thumb_image}>
      <Wrapper>
        <div className="project-section-container">
          <div className="project-circle">
            <div className="project-presentation">
              <img
                src="/images/course/project-presentation.png"
                alt="Sara presentando el proyecto a desarrollar durante el curso"
                className="project-presenter"
                width="588"
                height="356"
                loading="lazy"
              />
              <div className="project-projection" />
            </div>
          </div>
          <div className="project-content">
            <Typography as="h2" color="var(--baseNeutral)" small="headline5" large="headline2" id="project-title">
              {title}
            </Typography>
            <ul className="project-descriptions">
              {isList ? (
                blocks[0].data.items.map((text) => (
                  <li className="project-description" key={text}>
                    {ReactHtmlParser(text)}
                  </li>
                ))
              ) : (
                blocks.map(({ data: { text } }) => (
                  <li className="project-description" key={text}>
                    {ReactHtmlParser(text)}
                  </li>
                ))
              )}
            </ul>
            <SecondaryButton fullWidth onClick={() => setShowProject(true)}>
              Ver proyecto del curso
            </SecondaryButton>
          </div>
        </div>
      </Wrapper>
      <CourseProjectModal showProject={showProject} setShowProject={setShowProject} />
    </CourseProjectStyled>
  )
}

CourseProject.propTypes = {
  setActive: PropTypes.func.isRequired,
}

export default CourseProject
